import type {
  DebugHealthStatus,
  RuntimeHealthSnapshot,
  SnapshotCollectContext,
} from './types';

export function evaluateRuntimeHealth(
  ctx: SnapshotCollectContext
): RuntimeHealthSnapshot {
  const checks: Array<{
    id: string;
    status: DebugHealthStatus;
    detail?: string;
  }> = [];
  const reasons: string[] = [];

  const push = (
    id: string,
    status: DebugHealthStatus,
    detail?: string
  ): void => {
    checks.push({ id, status, ...(detail ? { detail } : {}) });
    if (status === 'degraded' || status === 'unhealthy') {
      reasons.push(detail ? `${id}: ${detail}` : id);
    }
  };

  if (!ctx.runtimeInitialized) {
    push('runtime', 'unknown', 'Runtime not initialized');
  } else if (ctx.runtimeDestroyed) {
    push('runtime', 'unknown', 'Runtime destroyed');
  } else {
    push('runtime', 'healthy');
  }

  if (!ctx.transport.enabled) {
    push('transport', 'healthy', 'Transport disabled by config');
  } else if (!ctx.transport.state) {
    push('transport', 'unknown', 'Transport state unavailable');
  } else {
    const t = ctx.transport.state;
    if (t.circuitBreakerState === 'open') {
      push('circuitBreaker', 'unhealthy', 'Circuit breaker open');
    } else if (t.circuitBreakerState === 'half_open') {
      push('circuitBreaker', 'degraded', 'Circuit breaker half-open');
    } else {
      push('circuitBreaker', 'healthy');
    }

    if (t.online === false) {
      push('offline', 'degraded', 'Client offline');
    } else if (t.online === true) {
      push('offline', 'healthy');
    } else {
      push('offline', 'unknown');
    }

    if (typeof t.dlqCount === 'number' && t.dlqCount > 0) {
      push('dlq', 'degraded', `DLQ has ${t.dlqCount} entries`);
    } else {
      push('dlq', 'healthy');
    }
  }

  if (ctx.integrations.enabled && ctx.integrations.active === false) {
    push('integrations', 'degraded', 'Integrations enabled but inactive');
  } else {
    push('integrations', 'healthy');
  }

  if (
    ctx.behaviorViewport?.enabled === true &&
    ctx.behaviorViewport.available === false
  ) {
    push(
      'behaviorViewport',
      'degraded',
      'BEHAVIOR_VIEWPORT_UNAVAILABLE: IntersectionObserver not available'
    );
  }

  const engagement = ctx.behaviorEngagement;
  if (engagement?.enabled === true && engagement.running === true) {
    // Drift excessivo do scheduler ou falha isolada do calculator degradam
    // apenas o engine opcional, sem tornar o Pixel inteiro unhealthy.
    const driftBudget = engagement.evaluationIntervalMs;
    const drift = engagement.lastDriftMs;
    if (engagement.hasError) {
      push(
        'behaviorEngagement',
        'degraded',
        'BEHAVIOR_ENGAGEMENT_CALC_ERROR: score calculation failed'
      );
    } else if (
      typeof drift === 'number' &&
      driftBudget > 0 &&
      drift > driftBudget
    ) {
      push(
        'behaviorEngagement',
        'degraded',
        `BEHAVIOR_ENGAGEMENT_DRIFT: scheduler drift ${drift}ms`
      );
    }
  }

  const rateLimit = ctx.behaviorRateLimit;
  if (rateLimit?.enabled === true) {
    const total = rateLimit.accepted + rateLimit.dropped;
    const dropRate = total > 0 ? rateLimit.dropped / total : 0;
    if (dropRate >= 0.5 && rateLimit.dropped > 0) {
      push(
        'behaviorRateLimit',
        'degraded',
        `BEHAVIOR_RATE_LIMIT_HIGH_DROP: ${rateLimit.dropped}/${total} dropped`
      );
    }
  }

  const buffer = ctx.behaviorBuffer;
  if (buffer?.enabled === true && buffer.maxSize > 0) {
    if (buffer.size >= buffer.maxSize) {
      push(
        'behaviorBuffer',
        'degraded',
        'BEHAVIOR_BUFFER_FULL: buffer reached maxSize'
      );
    }
  }

  const stateMachine = ctx.behaviorStateMachine;
  if (
    stateMachine?.enabled === true &&
    stateMachine.invalidTransitionCount > 0
  ) {
    push(
      'behaviorStateMachine',
      'degraded',
      `BEHAVIOR_STATE_INVALID_TRANSITIONS: ${stateMachine.invalidTransitionCount}`
    );
  }

  let status: DebugHealthStatus = 'healthy';
  if (checks.some(c => c.status === 'unhealthy')) status = 'unhealthy';
  else if (checks.some(c => c.status === 'degraded')) status = 'degraded';
  else if (checks.every(c => c.status === 'unknown')) status = 'unknown';
  else if (
    checks.some(c => c.status === 'unknown') &&
    !ctx.runtimeInitialized
  ) {
    status = 'unknown';
  }

  return { status, checks, reasons };
}
